<template>
  <div class="col column full-height">
    <div class="col-auto q-px-sm tw-pb-2">
      <div class="row height-30px">
        <div class="col">
          <div class="row full-height content-center">
            <SearchIcon class="search-icon q-ma-sm absolute" />

            <input
              data-testid="atendimento-busca"
              type="text"
              class="tw-h-[30px] tw-w-full tw-rounded-full tw-border tw-border-solid tw-border-line tw-bg-white tw-pl-8 tw-pr-3 tw-text-xs tw-text-bluegrey-10 tw-outline-none tw-transition focus:tw-border-primary focus:tw-ring-2 focus:tw-ring-primary/15 placeholder:tw-text-bluegrey-10/50"
              v-model="filter"
              placeholder="número, contato, email"
            >
          </div>
        </div>
      </div>
    </div>

    <div class="col column">
      <div class="col-auto q-py-xs full-width text-center upLoading">
        <q-spinner-dots
          v-if="isLoading"
          color="primary"
          indeterminate
        />
      </div>

      <AttendanceItemsGroup
        :json-current="current"
        :json-history="history"

        @onClick="handleClick"
        @onDblClick="handleDoubleClick"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import SearchIcon from '@/assets/icons/search.svg'
import AttendanceItemsGroup from './AttendanceItemsGroup'
import { ROUTE_ATTENDANCE_ITEM, ROUTE_PREVIEW_CURRENT } from '@/router/constants'
import { VOICE_CHANNEL, MAIL_CHANNEL, TEXT_CHANNEL } from '@/constants/channels'
import { DIRECTION_IN } from '@/constants/directions'
import { retryStartCall } from '@/libs/startAttendance'

export default {
  components: {
    SearchIcon,
    AttendanceItemsGroup
  },

  data () {
    return {
      filter: '',
      current: '[]',
      history: '[]'
    }
  },

  computed: {
    ...mapGetters({
      isAdmin: 'settingsModule/isAdmin',
      ongoingCallInPreview: 'attendanceModule/ongoingCallInPreview',
      queueProperties: 'agentModule/queueProperties'
    }),

    ...mapState({
      attendances: ({ attendanceModule }) => Object.assign({}, attendanceModule.history, attendanceModule.current),
      attendanceState: ({ attendanceModule }) => ({ ...attendanceModule }),
      isLoading: (state) => Boolean(state.agentModule?.session?.loading)
    }),

    isEmpty () {
      const currentEmpty = this.current === '[]'
      const historyEmpty = this.history === '[]'
      if (currentEmpty && historyEmpty) return true
      return false
    }
  },

  mounted () {
    this.$nextTick(() => {
      this.fetchMissingQueueProperties()
      this.listAttendances(this.filter)
    })
  },

  methods: {
    ...mapActions({
      updateHistoryAttendance: 'attendanceModule/updateHistoryAttendance',
      getQueuesProperties: 'agentModule/getQueuesProperties',
      clearChatUnreadMark: 'attendanceModule/clearChatUnreadMark',
      sendUnreadUpdate: 'socketModule/sendUnreadUpdate'
    }),

    async fetchMissingQueueProperties () {
      const allAttendances = Object.values(this.attendances)
      const queuesSet = new Set()

      allAttendances.forEach(att => {
        const queueId = att.queue ?? att.queueId
        if (!queueId) return
        if (queuesSet.has(queueId)) return

        const queueProps = this.queueProperties(queueId)
        if (Object.keys(queueProps).length === 0) {
          queuesSet.add(queueId)
        }
      })

      if (queuesSet.size > 0) {
        const queueList = Array.from(queuesSet).join(',')
        await this.getQueuesProperties(queueList)
      }
    },

    handleClick (attendanceId) {
      const attendance = this.attendances[attendanceId]
      if (!attendance) return

      if (attendance.channel === TEXT_CHANNEL && attendance.manualUnread) {
        this.clearChatUnreadMark(attendanceId)
        this.sendUnreadUpdate({ chatId: attendanceId, manualUnread: false })
      }

      const attendanceInPreview = Array.from(this.$store.state.attendanceModule.preview.attendances)[0] === attendanceId

      if (attendanceInPreview) {
        const queueId = attendance.queue || attendance.queueId
        this.$router.push({ name: ROUTE_PREVIEW_CURRENT, params: { attendanceId, queueId } }).catch(() => {})
        return
      }

      this.$router.push({ name: ROUTE_ATTENDANCE_ITEM, params: { attendanceId } }).catch(() => {})
    },

    async handleDoubleClick (attendanceId) {
      const vm = this

      const oldAttendance = vm.attendances[attendanceId]
      if (!oldAttendance) return

      if (oldAttendance.channel !== 'VOICE') return

      const queueProps = this.queueProperties(oldAttendance.queue ?? oldAttendance.queueId)
      const isAnonymizationEnabled = queueProps?.anonymizePhone === 'yes' || oldAttendance?.shouldAnonymize

      const newAttendance = await retryStartCall(vm, oldAttendance, isAnonymizationEnabled)
      if (!newAttendance) return

      const oldCallType = String(oldAttendance.callTypesName || '').trim().toUpperCase()
      const newCallType = String(newAttendance.callTypesName || '').trim().toUpperCase()
      const changedCallType = oldCallType !== newCallType

      if (changedCallType) {
        oldAttendance.callTypesName = newCallType
        vm.updateHistoryAttendance(newAttendance)
      }
    },

    listAttendances (filter) {
      const { conference } = this.attendanceState
      const current = toAttendancesList(this.attendanceState.current, filter, true).map(withLastMessage(conference))
      const history = toAttendancesList(this.attendanceState.history, filter).map(withLastMessage())
      const jsonCurrent = JSON.stringify(current)
      const jsonHistory = JSON.stringify(history)
      if (this.current !== jsonCurrent) this.current = jsonCurrent
      if (this.history !== jsonHistory) this.history = jsonHistory
    }
  },

  watch: {
    filter: {
      deep: true,
      handler (filter) {
        if (this.debounce) clearTimeout(this.debounce)
        this.debounce = setTimeout(() => this.listAttendances(filter), 300)
      }
    },

    attendanceState: {
      deep: true,
      handler () {
        if (this.debounce) clearTimeout(this.debounce)
        this.debounce = setTimeout(() => this.listAttendances(this.filter), 300)
      }
    }
  }
}

const withLastMessage = (conference = []) => ({ messages, ...item }) => {
  const list = Array.isArray(messages) ? [...messages].reverse() : []
  item.message = list.find(({ direction }) => direction === 'IN')
  if (!item.message && item.lastInMessage) {
    item.message = { direction: 'IN', type: item.lastInMessageType || 'TEXT', message: item.lastInMessage }
  }
  item.onConference = messages ? conference.includes(messages.id) : false
  return item
}

const toAttendancesList = (attendances, filter, prioritizeUnread = false) => {
  const arg = String(filter || '').trim().toLowerCase()

  return Object.values(attendances).filter((attendance) => {
    if (!arg) return true

    if (attendance.channel === MAIL_CHANNEL) {
      let to = ''
      let cc = ''
      let from = ''
      let message = ''
      let agents = ''
      let subjects = ''

      const attachs = Array.isArray(attendance.oldAttachments) ? attendance.oldAttachments.map(({ name }) => name).join(',') : []

      if (Array.isArray(attendance.emails)) {
        attendance.emails.forEach((email) => {
          from += `,${email.mailFrom || ''}`
          agents += `,${email.agentName || ''}`
          message += `${email.mailBody || ''}`
          to += Array.isArray(email.mailTo) ? `,${email.mailTo.map(({ id }) => id).join(',')}` : email.mailTo
          cc += Array.isArray(email.mailCc) ? `,${email.mailCc.map(({ id }) => id).join(',')}` : email.mailCc
          subjects += `,${email.subjects || ''}`
        })
      }

      return `${to},${cc},${from},${message},${agents},${attachs},${subjects}`.trim().toLocaleLowerCase().includes(arg)
    }

    if (attendance.channel === VOICE_CHANNEL) {
      const contact = String(attendance.contact || '').trim().toLocaleLowerCase().includes(arg)
      const queue = String(attendance.queue || '').trim().toLocaleLowerCase().includes(arg)
      return contact || queue
    }

    if (attendance.channel === TEXT_CHANNEL) {
      const { name, queueName, sourceId } = attendance
      let messages = ''

      if (Array.isArray(attendance.messages)) {
        messages = attendance.messages.map(({ agentName, message }) => {
          return agentName ? `${agentName} ${message}` : message
        }).join(',')
      }

      return `${name},${queueName},${sourceId},${messages}`.trim().toLocaleLowerCase().includes(arg)
    }

    return true
  }).map((attendance) => ({ ...attendance, hasUnread: hasUnreadMessages(attendance) })).sort((a, b) => {
    if (a.channel !== b.channel) return 0

    const textChannel = a.channel === 'TEXT' && b.channel === 'TEXT'

    if (textChannel) {
      if (prioritizeUnread && a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1

      const lastMessageTimeA = getlastMessageCreatedAt(a)
      const lastMessageTimeB = getlastMessageCreatedAt(b)
      return lastMessageTimeB - lastMessageTimeA
    }

    let startDateA = getStartDate(a)
    let startDateB = getStartDate(b)
    if (String(startDateA).length > 11) startDateA = Math.floor(startDateA / 1000)
    if (String(startDateB).length > 11) startDateB = Math.floor(startDateB / 1000)
    return startDateB - startDateA
  }).map((item) => {
    const { emails, messages, ...attendance } = item
    if (emails?.length) attendance.emails = [emails.at(-1)]
    if (messages?.length) {
      attendance.messages = [messages.at(-1)]
      attendance.unreadMessages = messages.filter((msg) => msg.direction === 'IN' && !msg.readedAt && msg.type !== 'REACTION').length
    }
    return attendance
  })
}

const hasUnreadMessages = (attendance) => {
  if (attendance.manualUnread) return true
  if (!Array.isArray(attendance.messages)) return false
  return attendance.messages.some((msg) => msg.direction === 'IN' && !msg.readedAt && msg.type !== 'REACTION')
}

const getStartDate = (attendance) => {
  if (attendance.channel === 'MAIL') return attendance.finishedAt ?? attendance.startAt ?? attendance.createdAt

  return attendance.direction === DIRECTION_IN
    ? attendance.answerAt || attendance.createdAt
    : attendance.createdAt
}

const getlastMessageCreatedAt = (attendance) => {
  if (!attendance.messages?.length) {
    return attendance.lastMessageAt ?? attendance.finishedAt ?? attendance.answeredAt ?? attendance.createdAt ?? 0
  }

  const lastMessage = attendance.messages[attendance.messages.length - 1]

  let lastMessageTime = attendance.direction === 'IN'
    ? lastMessage?.deliveredAt || lastMessage?.createdAt
    : lastMessage?.createdAt

  if (isNaN(lastMessageTime)) lastMessageTime = new Date(lastMessageTime).getTime()
  if (typeof lastMessageTime === 'string') Number(lastMessageTime).trim()

  return lastMessageTime
}

</script>

<style lang="sass" scoped>
.search-icon
  height: 16px
  width: 16px

.search-icon > stroke
  fill: #01253C66

.height-30px
  height: 30px
  min-height: 30px

.bg-blue
  background: #e3f2fd !important
</style>
